import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { refreshStepStatus } from "../actions/onboardingActions";
import { isOnboardingScan } from "./session-keys";
import {
  canAccessDashboard,
  isContinueResumeTarget,
  resumeRedirect,
} from "./user-access";

/**
 * Shared leave/return gate: when signed-in, not dashboard-ready, and not mid
 * onboarding, refresh server `step_status` then resolve the resume destination.
 * Stale tokens are handled reactively: any authenticated onboarding 401 clears
 * the session and reloads landing (see onboardingActions).
 */
export const useOnboardingGate = ({
  authed,
  loading,
  needsServerStatus,
  onContinue = false,
} = {}) => {
  const dispatch = useDispatch();
  const midOnboarding = isOnboardingScan();
  const [ready, setReady] = useState(!needsServerStatus);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    if (!needsServerStatus) {
      setReady(true);
      setRedirectTo(null);
      return undefined;
    }

    let cancelled = false;
    setReady(false);
    (async () => {
      await dispatch(refreshStepStatus());
      if (cancelled) {
        return;
      }
      if (onContinue && isContinueResumeTarget()) {
        setRedirectTo(null);
      } else {
        setRedirectTo(resumeRedirect());
      }
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, needsServerStatus, onContinue]);

  return {
    midOnboarding,
    ready,
    redirectTo,
    canAccessDashboard: canAccessDashboard(),
    authed: Boolean(authed),
    loading: Boolean(loading),
  };
};
